import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Divider,
  Avatar,
} from '@mui/material';
import {
  Business,
  People,
  TrendingUp,
  AttachMoney,
  CheckCircle,
  Star,
  LocalShipping,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

interface MembershipReportProps {
  cooperativeId?: string;
}

export const MembershipReport: React.FC<MembershipReportProps> = ({ 
  cooperativeId = 'COOP-AGR-001' 
}) => {
  // Mock data for cooperative operations
  const cooperativeData = {
    cooperativeId,
    name: 'Cooperativa Agro Sul',
    reportMonth: 'Novembro 2024',
    totalMembers: 127,
    activeMembers: 119,
    newMembers: 8,
    totalContributions: 'R$ 2.850.000',
    averageContribution: 'R$ 22.440',
    status: 'Ativo',
    president: 'Maria Santos',
    topMembers: [
      { name: 'Fazenda Bela Vista', contribution: 'R$ 285.000', percentage: 10.0, category: 'Ouro' },
      { name: 'Sítio Esperança', contribution: 'R$ 198.000', percentage: 6.9, category: 'Ouro' },
      { name: 'Rancho Grande', contribution: 'R$ 156.000', percentage: 5.5, category: 'Prata' },
      { name: 'Fazenda Progresso', contribution: 'R$ 142.000', percentage: 5.0, category: 'Prata' },
      { name: 'Outros (115 membros)', contribution: 'R$ 2.069.000', percentage: 72.6, category: 'Diversos' },
    ],
    services: [
      { service: 'Venda de Insumos', revenue: 'R$ 850.000', members: 98, satisfaction: 94 },
      { service: 'Assistência Técnica', revenue: 'R$ 320.000', members: 76, satisfaction: 96 },
      { service: 'Comercialização', revenue: 'R$ 1.200.000', members: 119, satisfaction: 91 },
      { service: 'Crédito Rural', revenue: 'R$ 480.000', members: 45, satisfaction: 89 },
    ],
    monthlyStats: [
      { category: 'Contribuições', current: 2850000, previous: 2720000, growth: 4.8 },
      { category: 'Membros Ativos', current: 119, previous: 115, growth: 3.5 },
      { category: 'Serviços Utilizados', current: 342, previous: 328, growth: 4.3 },
      { category: 'Satisfação Média', current: 92.5, previous: 90.1, growth: 2.7 },
    ],
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Ouro': return DESIGN_TOKENS.colors.status.warning[500];
      case 'Prata': return DESIGN_TOKENS.colors.neutral[400];
      case 'Bronze': return DESIGN_TOKENS.colors.status.warning[700];
      default: return DESIGN_TOKENS.colors.primary[500];
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Ouro': return <Star sx={{ color: DESIGN_TOKENS.colors.status.warning[500] }} />;
      case 'Prata': return <Star sx={{ color: DESIGN_TOKENS.colors.neutral[400] }} />;
      case 'Bronze': return <Star sx={{ color: DESIGN_TOKENS.colors.status.warning[700] }} />;
      default: return <People sx={{ color: DESIGN_TOKENS.colors.primary[500] }} />;
    }
  };

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: DESIGN_TOKENS.colors.organization.cooperative,
            mx: 'auto',
            mb: 2,
            fontSize: '2rem',
          }}
        >
          <Business sx={{ fontSize: '2.5rem' }} />
        </Avatar>
        <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.primary[700] }}>
          Relatório de Associados
        </Typography>
        <Typography variant="h6" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 1 }}>
          {cooperativeData.name}
        </Typography>
        <Chip 
          label={cooperativeData.status} 
          color="success" 
          sx={{ fontWeight: 600 }}
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Membership Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: DESIGN_TOKENS.colors.primary[700] }}>
            Resumo de Associados - {cooperativeData.reportMonth}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <People sx={{ color: DESIGN_TOKENS.colors.organization.cooperative }} />
                <Box>
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>Total de Membros</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{cooperativeData.totalMembers}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CheckCircle sx={{ color: DESIGN_TOKENS.colors.organization.cooperative }} />
                <Box>
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>Membros Ativos</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{cooperativeData.activeMembers}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AttachMoney sx={{ color: DESIGN_TOKENS.colors.organization.cooperative }} />
                <Box>
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>Total Contribuições</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{cooperativeData.totalContributions}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUp sx={{ color: DESIGN_TOKENS.colors.organization.cooperative }} />
                <Box>
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>Contribuição Média</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{cooperativeData.averageContribution}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Monthly Statistics */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: DESIGN_TOKENS.colors.primary[700] }}>
            Estatísticas Mensais
          </Typography>
          {cooperativeData.monthlyStats.map((stat, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {stat.category}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {stat.category.includes('R$') || stat.category.includes('Contribuições') 
                      ? `R$ ${(stat.current / 1000).toFixed(0)}k`
                      : stat.current.toLocaleString('pt-BR', { maximumFractionDigits: 1 })
                    }
                  </Typography>
                  <Chip
                    label={`+${stat.growth}%`}
                    size="small"
                    sx={{
                      bgcolor: `${DESIGN_TOKENS.colors.status.success[500]}20`,
                      color: DESIGN_TOKENS.colors.status.success[500],
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min((stat.current / (stat.previous * 1.2)) * 100, 100)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: DESIGN_TOKENS.colors.neutral[200],
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: DESIGN_TOKENS.colors.organization.cooperative,
                  },
                }}
              />
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Top Contributors */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: DESIGN_TOKENS.colors.primary[700] }}>
            Principais Contribuintes
          </Typography>
          {cooperativeData.topMembers.map((member, index) => (
            <Box key={index} sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              py: 2,
              borderBottom: index < cooperativeData.topMembers.length - 1 ? `1px solid ${DESIGN_TOKENS.colors.neutral[200]}` : 'none'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getCategoryIcon(member.category)}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {member.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                    {member.contribution}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.organization.cooperative }}>
                  {member.percentage}%
                </Typography>
                <Chip
                  label={member.category}
                  size="small"
                  sx={{
                    bgcolor: `${getCategoryColor(member.category)}20`,
                    color: getCategoryColor(member.category),
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>

      {/* Services Performance */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: DESIGN_TOKENS.colors.primary[700] }}>
            Performance dos Serviços
          </Typography>
          {cooperativeData.services.map((service, index) => (
            <Box key={index} sx={{ 
              py: 2,
              borderBottom: index < cooperativeData.services.length - 1 ? `1px solid ${DESIGN_TOKENS.colors.neutral[200]}` : 'none'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {service.service}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.organization.cooperative }}>
                  {service.revenue}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                  {service.members} membros utilizaram
                </Typography>
                <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.status.success[500], fontWeight: 600 }}>
                  {service.satisfaction}% satisfação
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={service.satisfaction}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: DESIGN_TOKENS.colors.neutral[200],
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: service.satisfaction >= 90 ? DESIGN_TOKENS.colors.status.success[500] : service.satisfaction >= 80 ? DESIGN_TOKENS.colors.status.warning[500] : DESIGN_TOKENS.colors.status.error[500],
                  },
                }}
              />
            </Box>
          ))}
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}` }}>
            <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
              Presidente: {cooperativeData.president}
            </Typography>
            <br />
            <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
              Relatório gerado em: {cooperativeData.reportMonth}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
